import {reactFetchJSON} from '@github-ui/verified-fetch'

import type {ImplicitContextPage} from '../contexts/ImplicitContextPageContext'
import type {ImplicitReference} from './implicit-context'

/**
 * Resolves the current page into `CopilotChatReference`(s) via the same-origin
 * dotcom endpoint (`Copilot::Chat::ContextController#show`). The endpoint runs
 * Rails route recognition on `url`, so it correctly ignores non-entity pages
 * (returning `null`) — unlike naive client-side path parsing. Uses
 * `reactFetchJSON` so the request carries the `GitHub-Is-React` header for
 * `request.dist.referrer` tracking.
 *
 * Authentication uses the verified cookie + CSRF fetch. The same-origin dotcom
 * endpoint does not require an `X-Copilot-Api-Token` header.
 *
 * Returns a normalized array: the endpoint may respond with a single reference,
 * an array of references, or `null` (no implicit context for this page).
 */
export async function fetchImplicitReferences(
  {owner, repo, url}: ImplicitContextPage,
  signal?: AbortSignal,
): Promise<ImplicitReference[]> {
  // Non-repository pages carry empty owner/repo: the endpoint is repository-
  // scoped (`implicit-context/:owner/:repo/:url`) and can't resolve them, so
  // skip the request entirely. The `github_url` fallback is synthesized from the
  // page URL downstream instead.
  if (!owner || !repo) return []

  const path = `/github-copilot/chat/implicit-context/${owner}/${repo}/${encodeURIComponent(url)}`

  const response = await reactFetchJSON(path, {method: 'GET', signal})

  // A 404 means the guessed repo/page has no resolvable implicit context (e.g. the
  // client-side path fallback pointed at a non-repository page). Treat it as "no
  // context" rather than an error so implicit context silently stays empty.
  if (response.status === 404) return []

  if (!response.ok) {
    throw new Error(`Failed to load implicit context (${response.status})`)
  }

  const data = (await response.json()) as ImplicitReference | ImplicitReference[] | null
  if (data == null) return []
  return Array.isArray(data) ? data : [data]
}
